import { CartUpdate, MyCartUpdate } from "@commercetools/platform-sdk";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Session } from "next-auth";
import { getSession, signIn, useSession } from "next-auth/react";
import PQueue from "p-queue";
import { toast } from "react-toastify";

import { updateCartById } from "@/app/actions";
import { updateTOSessionWithPromotions as updateTalonSessionWithPromotions } from "@/clients/talon-one/cart";
import { fetchCart } from "@/features/basket/hooks/use-cart";
import { revalidateCheckout } from "@/features/checkout/helpers/revalidate-checkout";
import { UseArtworkCheck, UseArtworkChecks } from "./use-cart-addon";

export interface RemoveCartInputType {
  cartVersion: number;
  cartId: string;
  lineItemId: string;
}

const removeCartItemQueue = new PQueue({ concurrency: 1 });

async function processRemoveCartItem(session: Session | null, lineItemId: string) {
  const cart = await fetchCart(session);

  if (!cart) {
    throw new Error("Cart not found");
  }

  const body: MyCartUpdate & CartUpdate = {
    version: cart.version,
    actions: [
      {
        action: "removeLineItem",
        lineItemId: lineItemId,
      },
    ],
  };
  const lineItem = cart.lineItems.find((item) => item.id === lineItemId);
  const artworkCheck = UseArtworkCheck(cart.customLineItems, lineItem?.key ?? "");
  if (artworkCheck) {
    body.actions.push({
      action: "removeCustomLineItem",
      customLineItemId: artworkCheck!.id,
    });
  }
  const newCart = await updateCartById(cart.id, body);

  const printPerksUsedAmount = UseArtworkChecks(cart.customLineItems)
    ?.filter((item) => item.id !== artworkCheck?.id)
    .reduce((acc, item) => acc + (item.custom?.fields.printPerksAmountUsed ?? 0), 0);

  await updateTalonSessionWithPromotions({
    cart: newCart,
    promotions: { printPerksUsedAmount, cashbackAmountUsed: 0 },
  });
}

async function removeCartItem(session: Session | null, input: RemoveCartInputType) {
  if (!session) {
    await signIn("guest", { redirect: false });
    session = await getSession();
  }

  await removeCartItemQueue.add(() => processRemoveCartItem(session, input.lineItemId));
  await removeCartItemQueue.onIdle();
}

export function useRemoveCartItemMutation() {
  const { data: session } = useSession();
  const queryClient = useQueryClient();
  return useMutation((input: RemoveCartInputType) => removeCartItem(session, input), {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["CART"] });
      revalidateCheckout();
    },
    onError: (data: Error) => {
      toast.error("Error removing cart item: " + data.message);
    },
  });
}
